/**
 * This file provides a React Context and a custom hook (`useTelemetry`) to manage the telemetry ID
 * required to request fingerprints from Stytch.
 *
 * How It Works:
 * - Fetches a `telemetryId` from the Stytch API when the booking form is opened.
 * - Stores the `telemetryId` in Redux state so it can be passed to the backend on booking submit.
 * - Automatically refreshes the `telemetryId` 15 seconds before it expires (5 minutes).
 * - Allows manual refreshing via `refreshTelemetry` when needed.
 *
 * Why Use This?
 * - Ensures a valid `telemetryId` is always available without slowing down form submission.
 * - Avoids issues where the 'telemetryId' expires before booking submission.
 * - Prevents duplicate requests by centralizing the logic in a context.
 */

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Settings } from '../services/settings'

const TelemetryContext = createContext(undefined)

const TELEMETRY_EXPIRATION_TIME = 5 * 60 * 1000 // 5 minutes
const REFRESH_BEFORE_EXPIRY = Settings?.trust_and_safety?.stytch_telemetry_expiry_buffer || 15_000 // 15 seconds
const MAX_REFRESH_ATTEMPTS = Settings?.trust_and_safety?.stytch_max_telemetry_refresh_attempts || 20
const STYTCH_TELEMETRY_FAILED_ERROR_ID = '4fd394a2-bc99-47c5-86d2-64414ee3d1db' // hard-coded response from Stytch
const MAX_FETCH_TELEMETRY_RETRIES = Settings?.trust_and_safety?.stytch_max_fetch_telemetry_retries || 3

export const TelemetryProvider = ({ children }) => {
  const dispatch = useDispatch()

  // Using Redux state:
  const telemetryId = useSelector((state) => state.tracking?.stytch_telemetry_id)
  const expiresAt = useSelector((state) => state.tracking?.stytch_telemetry_expires_at)

  // Local state:
  const [refreshCount, setRefreshCount] = useState(0)

  /**
   *  Generate fingerprint request for prospective invitee. Fetches a new telemetry ID from
   *  Stytch's API and stores in state so that the fingerprints may be "looked up" later on
   *  the backend.
   */
  const fetchTelemetryId = useCallback(async (currentRefreshCount = 0) => {
    if (currentRefreshCount >= MAX_REFRESH_ATTEMPTS) return

    const submitURL = 'https://dfp.calendly.com'
    const publicToken = Settings?.trust_and_safety?.stytch_public_token

    if (!publicToken) return

    // Check if GetTelemetryID is available
    if (typeof window.GetTelemetryID !== 'function') {
      console.warn('GetTelemetryID function is not available - telemetry script may not have loaded')
      return
    }

    const fetchTelemetryIdWithRetry = async (retryCount = 0) => {
      // Call GetTelemetryID with separate parameters (not an object)
      let newTelemetryId = await window.GetTelemetryID(publicToken, submitURL)

      if (retryCount >= MAX_FETCH_TELEMETRY_RETRIES) {
        return newTelemetryId
      }

      if (newTelemetryId === STYTCH_TELEMETRY_FAILED_ERROR_ID) {
        await new Promise((resolve) => setTimeout(resolve, 300))
        newTelemetryId = await fetchTelemetryIdWithRetry(retryCount + 1)
      }

      return newTelemetryId
    }

    try {
      const newTelemetryId = await fetchTelemetryIdWithRetry(0)

      if (!newTelemetryId) return

      // Update refresh count locally
      setRefreshCount(currentRefreshCount + 1)

      // Update Redux state with the new telemetryId and its TTL
      dispatch({
        type: 'TRACK_STYTCH_TELEMETRY_ID',
        payload: {
          telemetryId: newTelemetryId,
          expiresAt: Date.now() + TELEMETRY_EXPIRATION_TIME,
        }
      })
    } catch (error) {
      console.error('Telemetry ID fetch failed:', error)
    }
  }, [dispatch])

  const refreshTelemetry = useCallback(() => fetchTelemetryId(), [fetchTelemetryId])

  const contextValue = useMemo(() => ({ telemetryId, refreshTelemetry }), [telemetryId, refreshTelemetry])

  /**
   * Fetch telemetry ID on mount.
   * Sets currentRefreshCount to 0
   * Ensures telemetry ID is available as soon as the component loads.
   */
  useEffect(() => {
    fetchTelemetryId(0)
  }, [fetchTelemetryId])

  /**
   * Automatically refreshes the telemetry ID before expiration.
   * - Runs only if telemetryId is set and hasn't exceeded MAX_REFRESH_ATTEMPTS.
   * - Schedules a refresh 15 seconds before expiration.
   * - Uses `expiresAt` from Redux state
   */
  useEffect(() => {
    if (!expiresAt || refreshCount >= MAX_REFRESH_ATTEMPTS) return undefined

    const timeUntilRefresh = expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY

    if (timeUntilRefresh > 0) {
      const timer = setTimeout(() => fetchTelemetryId(refreshCount), timeUntilRefresh)
      return () => clearTimeout(timer)
    }

    return undefined
  }, [expiresAt, refreshCount, fetchTelemetryId])


  return <TelemetryContext.Provider value={contextValue}>{children}</TelemetryContext.Provider>
}

export const useTelemetry = () => useContext(TelemetryContext) || { telemetryId: null, refreshTelemetry: () => {} }
